/**
 * Search results noindex/index decisions (Milestone 14).
 */
import {
  DEALER_DIRECTORY_DEFAULT_SORT,
  hasActiveDealerDirectoryFilters,
} from "../constants/dealerDirectoryFilterParamsConstants";
import { DEALER_DIRECTORY_PAGE_STATUS } from "../constants/dealerDirectoryPageConstants";
import { VEHICLE_SEARCH_SORT } from "../constants/vehicleSearchParamsConstants";
import { normalizePath } from "./canonicalUrls";
import { resolvePublicListingStorageId } from "../utils/automapListing/resolveCanonicalVehiclePublicUrl";

const CURATED_SEO_ALIAS_PREFIXES = [
  "/carros-usados-",
  "/camionetas-usadas-",
  "/carros-automaticos-",
  "/carros-financiados-",
  "/carros-economicos-",
  "/automoviles-usados-",
  "/pickups-usadas-",
  "/carros-familiares-",
];

const MEANINGFUL_FILTER_KEYS = ["city", "make", "model", "bodyType", "transmission", "priceMinCop", "priceMaxCop"];

function isCuratedSeoAliasPath(pathname) {
  const path = normalizePath(pathname);
  return CURATED_SEO_ALIAS_PREFIXES.some((prefix) => path.startsWith(prefix));
}

function countMeaningfulFilters(searchParams = {}) {
  return MEANINGFUL_FILTER_KEYS.reduce((count, key) => {
    const value = searchParams[key];
    if (value == null || value === "" || value === false) return count;
    return count + 1;
  }, 0);
}

function hasNonDefaultSort(searchParams = {}) {
  const sort = searchParams.sort || VEHICLE_SEARCH_SORT.RECOMMENDED;
  return sort !== VEHICLE_SEARCH_SORT.RECOMMENDED;
}

function hasRiskOrAdvancedFilters(searchParams = {}) {
  return Boolean(
    searchParams.keyword ||
      searchParams.department ||
      searchParams.yearMin ||
      searchParams.yearMax ||
      searchParams.mileageMinKm ||
      searchParams.mileageMaxKm ||
      searchParams.fuelType ||
      searchParams.dealerVerified ||
      searchParams.fairPriceLabels?.length ||
      searchParams.riskLevels?.length ||
      searchParams.hideHighRisk ||
      searchParams.recentlyConfirmedOnly ||
      searchParams.hasPhotos ||
      searchParams.availabilityStatus ||
      searchParams.completeInfoOnly
  );
}

/**
 * @returns {{ shouldIndex: boolean, reason: string }}
 */
export function getSearchNoindexDecision({
  pathname = "",
  searchParams = {},
  resultCount = null,
  seoAliasConfig = null,
  isInvalidFilterCombo = false,
} = {}) {
  if (seoAliasConfig?.noindex === true) {
    return { shouldIndex: false, reason: "seo_alias_noindex" };
  }

  if (isCuratedSeoAliasPath(pathname) || seoAliasConfig) {
    if (resultCount === 0) return { shouldIndex: false, reason: "empty_seo_alias" };
    return { shouldIndex: true, reason: "curated_seo_alias" };
  }

  if (searchParams.keyword) {
    return { shouldIndex: false, reason: "keyword_search" };
  }

  if ((searchParams.page || 1) > 1) {
    return { shouldIndex: false, reason: "paginated_search" };
  }

  if (hasNonDefaultSort(searchParams) && countMeaningfulFilters(searchParams) === 0) {
    return { shouldIndex: false, reason: "sort_only" };
  }

  if (hasRiskOrAdvancedFilters(searchParams)) {
    return { shouldIndex: false, reason: "advanced_filters" };
  }

  const meaningfulCount = countMeaningfulFilters(searchParams);
  if (meaningfulCount > 2) {
    return { shouldIndex: false, reason: "deep_filtered_query" };
  }

  if (isInvalidFilterCombo) {
    return { shouldIndex: false, reason: "invalid_filter_combo" };
  }

  if (resultCount === 0) {
    return { shouldIndex: false, reason: "empty_results" };
  }

  return { shouldIndex: true, reason: "indexable_search" };
}

/**
 * @returns {{ shouldIndex: boolean, follow: boolean, reason: string }}
 */
export function getVehicleNoindexDecision({
  listing = null,
  dealer = null,
  publicStatus = "not_found",
  status = "not_found",
} = {}) {
  if (status === "not_found" || publicStatus === "not_found" || !listing?.id) {
    return { shouldIndex: false, follow: true, reason: "not_found" };
  }

  if (publicStatus === "hidden") {
    return { shouldIndex: false, follow: true, reason: "hidden_listing" };
  }

  if (publicStatus === "unavailable") {
    return { shouldIndex: false, follow: true, reason: "sold_unavailable" };
  }

  if (!resolvePublicListingStorageId(listing)) {
    return { shouldIndex: false, follow: true, reason: "missing_mobility_listing_id" };
  }

  const hasIdentity = Boolean(listing.make || listing.model || listing.title);
  if (!hasIdentity) {
    return { shouldIndex: false, follow: true, reason: "insufficient_public_data" };
  }

  const dealerStatus = dealer?.status || dealer?.publicStatus;
  if (["suspended", "rejected", "inactive", "archived"].includes(dealerStatus)) {
    return { shouldIndex: false, follow: true, reason: "dealer_blocked" };
  }

  return { shouldIndex: true, follow: true, reason: "active_listing" };
}

export function getChecklistNoindexDecision({ hasVehicleQueryContext = false } = {}) {
  if (hasVehicleQueryContext) {
    return { shouldIndex: false, follow: true, reason: "vehicle_query_context" };
  }
  return { shouldIndex: true, follow: true, reason: "checklist_hub" };
}

/**
 * @returns {{ shouldIndex: boolean, follow: boolean, reason: string }}
 */
export function getDealerDirectoryNoindexDecision({
  pageState = "ready",
  directoryStatus = DEALER_DIRECTORY_PAGE_STATUS.SUCCESS,
  visibleCount = 0,
  filterParams = {},
} = {}) {
  if (pageState === "loading") {
    return { shouldIndex: false, follow: true, reason: "loading" };
  }

  if (pageState === "error" || directoryStatus === DEALER_DIRECTORY_PAGE_STATUS.ERROR) {
    return { shouldIndex: false, follow: true, reason: "error" };
  }

  if (directoryStatus === DEALER_DIRECTORY_PAGE_STATUS.EMPTY || visibleCount === 0) {
    return { shouldIndex: false, follow: true, reason: "empty_directory" };
  }

  if (hasActiveDealerDirectoryFilters(filterParams)) {
    return { shouldIndex: false, follow: true, reason: "filtered_query" };
  }

  const sort = filterParams.sort || DEALER_DIRECTORY_DEFAULT_SORT;
  if (sort !== DEALER_DIRECTORY_DEFAULT_SORT) {
    return { shouldIndex: false, follow: true, reason: "non_default_sort" };
  }

  if ((filterParams.page || 1) > 1) {
    return { shouldIndex: false, follow: true, reason: "paginated_directory" };
  }

  return { shouldIndex: true, follow: true, reason: "indexable_directory" };
}

export function getDealerNoindexDecision({ dealer = null, viewState = "ready" } = {}) {
  if (!dealer || viewState === "unavailable" || viewState === "not_found") {
    return { shouldIndex: false, follow: true, reason: "dealer_unavailable" };
  }

  const status = dealer.status || dealer.publicStatus;
  if (["suspended", "rejected", "inactive", "archived", "hidden"].includes(status)) {
    return { shouldIndex: false, follow: true, reason: "dealer_blocked" };
  }

  return { shouldIndex: true, follow: true, reason: "active_dealer" };
}

/** Unified noindex entry point. */
export function getNoindexDecision(context = {}) {
  const pageType = context.pageType || "search";

  if (pageType === "vehicle_detail") {
    return getVehicleNoindexDecision(context);
  }
  if (pageType === "checklist") {
    return getChecklistNoindexDecision(context);
  }
  if (pageType === "dealer") {
    return getDealerNoindexDecision(context);
  }
  if (pageType === "dealer_directory") {
    return getDealerDirectoryNoindexDecision(context);
  }

  const searchDecision = getSearchNoindexDecision(context);
  return { ...searchDecision, follow: true };
}

export { isCuratedSeoAliasPath, countMeaningfulFilters };
